import { useEffect, useRef, type RefObject } from 'react';

export type OrbState = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

const LABELS: Record<OrbState, string> = {
  idle: 'Standing by',
  listening: 'Listening',
  transcribing: 'Hearing you',
  thinking: 'Thinking',
  speaking: 'Speaking',
};

/**
 * Alfred's HUD orb: thin rotating rings, a scanning arc and a solid core. Always moving: it
 * breathes on standby, spins up while he thinks, and pulses while he speaks (with the voice's
 * loudness when there is one, a steady rhythm when the reply is text). Calm under reduced motion.
 */
export function RingOrb({
  state,
  level,
  showLabel = true,
}: {
  state: OrbState;
  /** 0..1 loudness from the voice session (mic while listening, Alfred while speaking). */
  level?: RefObject<number>;
  showLabel?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Loudness changes every frame: written to a CSS variable, never React state.
  useEffect(() => {
    if (!level || (state !== 'listening' && state !== 'speaking')) return;
    let frame = 0;
    const tick = () => {
      ref.current?.style.setProperty('--level', String(level.current ?? 0));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      ref.current?.style.setProperty('--level', '0');
    };
  }, [level, state]);

  return (
    <div className={`ring-orb ring-orb-${state}`} ref={ref}>
      <svg viewBox="0 0 320 320" aria-hidden="true">
        <circle className="ro-outer" cx="160" cy="160" r="150" />
        <circle className="ro-dash" cx="160" cy="160" r="122" />
        <circle className="ro-inner" cx="160" cy="160" r="96" />
        <path className="ro-arc" d="M160 10 A150 150 0 0 1 290 85" />
        <circle className="ro-core" cx="160" cy="160" r="44" />
      </svg>
      {showLabel && (
        <span className="ring-orb-label" role="status">
          {LABELS[state]}
        </span>
      )}
    </div>
  );
}

/** Alfred's mark (as on the app icon): a ring, a faint inner ring and a solid core. */
export function AlfredMark({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" className="alfred-mark">
      <circle cx="16" cy="16" r="13" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle
        cx="16"
        cy="16"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.45"
        strokeWidth="0.8"
      />
      <circle cx="16" cy="16" r="4.6" fill="currentColor" />
    </svg>
  );
}
